import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Box,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  useTheme
} from '@mui/material';
import {
  Chat as ChatIcon,
  Lock as LockIcon,
  Star as StarIcon
} from '@mui/icons-material';
import GameChatDialog from './chat/GameChatDialog';
import Promos from './Promos';
import SportsNews from './SportsNews';
import { SportIcon } from '../utils/sportIcons';
import { fetchRealPeriodMarketsForGame, mergeRealPeriodMarketsIntoGame } from '../services/oddsApi';
import SportsbookPreferences from '../services/sportsbookPreferences';
import TeamBadge from './TeamBadge';
import FavoriteTeamService from '../services/favoriteTeamService';
import { useAuth } from '../contexts/AuthContext';
import BestOddsService from '../services/BestOddsService';

const OddsList = ({ odds, selectedSport, isHomeScreen = false, maxGames = null, onOddsClick = null, isHandicapperMode = false }) => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  
  // Helper function to get default market based on sport
  const getDefaultMarket = () => {
    const isBaseball = selectedSport && (selectedSport.title.includes('MLB') || selectedSport.title.includes('MiLB'));
    return isBaseball ? 'h2h' : 'spreads';
  };

  const [selectedGame, setSelectedGame] = useState(null);
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [globalActiveMarket, setGlobalActiveMarket] = useState(getDefaultMarket()); // Global market selection for all games
  const [sportsbookOrder, setSportsbookOrder] = useState([]);
  const [loadingPeriodMarkets, setLoadingPeriodMarkets] = useState(new Set()); // Track which games are loading period markets
  const [gamesWithRealPeriodMarkets, setGamesWithRealPeriodMarkets] = useState(new Map()); // Store games with real period data
  const [favoriteTeams, setFavoriteTeams] = useState({});

  // Helper function to get the games that will actually be displayed for a specific market
  const getVisibleGamesForMarket = (marketType, applyMaxGamesLimit = true) => {
    // Process games, using real period market data when available
    const processedGames = odds.map(game => {
      return gamesWithRealPeriodMarkets.get(game.id) || game;
    });

    // Filter out games that don't have any valid odds for any market
    let gamesWithOdds = processedGames.filter(game => {
      const hasMoneyline = getBookmakersForGame(game, 'h2h').length > 0;
      const hasSpreads = getBookmakersForGame(game, 'spreads').length > 0;
      const hasTotals = getBookmakersForGame(game, 'totals').length > 0;
      return hasMoneyline || hasSpreads || hasTotals;
    });

    // Filter by the specific market type
    let filteredGames = gamesWithOdds.filter(game => getBookmakersForGame(game, marketType).length > 0);
    
    // Apply maxGames limit if specified and requested
    if (applyMaxGamesLimit && maxGames && maxGames > 0) {
      filteredGames = filteredGames.slice(0, maxGames);
    }
    return filteredGames;
  };

  // Helper function to get odds for totals outcome (Over/Under)
  const getOddsForTotalsOutcome = (game, bookmakerTitle, outcomeName, marketType) => {
    const bookmaker = game.bookmakers?.find(b => b.title === bookmakerTitle);
    if (!bookmaker) return null;

    const market = bookmaker.markets?.find(m => m.key === marketType);
    if (!market) return null;

    return market.outcomes?.find(outcome => outcome.name === outcomeName);
  };



  // Load sportsbook order from preferences on component mount
  useEffect(() => {
    const loadSportsbookOrder = () => {
      const order = SportsbookPreferences.getSportsbookNamesSync();
      setSportsbookOrder(order);
    };

    loadSportsbookOrder();

    // Listen for sportsbook order changes (when user updates settings)
    const handleOrderChange = () => {
      loadSportsbookOrder();
    };

    window.addEventListener('sportsbookOrderChanged', handleOrderChange);

    // Cleanup event listener
    return () => {
      window.removeEventListener('sportsbookOrderChanged', handleOrderChange);
    };
  }, []);

  // Update default market when sport changes
  useEffect(() => {
    const getDefaultMarket = () => {
      const isBaseball = selectedSport && (selectedSport.title.includes('MLB') || selectedSport.title.includes('MiLB'));
      return isBaseball ? 'h2h' : 'spreads';
    };
    
    setGlobalActiveMarket(getDefaultMarket());
  }, [selectedSport]);

  // Load favorite teams
  useEffect(() => {
    const loadFavoriteTeams = async () => {
      try {
        const teams = await FavoriteTeamService.getFavoriteTeams();
        setFavoriteTeams(teams);
      } catch (error) {
        console.error('Error loading favorite teams:', error);
      }
    };

    loadFavoriteTeams();
  }, [currentUser]);

  // Helper function to check if a team is a favorite
  const isFavoriteTeam = (teamName) => {
    const sportTitle = selectedSport?.title || '';
    let sportKey = '';
    
    if (sportTitle.includes('NFL')) sportKey = 'NFL';
    else if (sportTitle.includes('NBA')) sportKey = 'NBA';
    else if (sportTitle.includes('MLB')) sportKey = 'MLB';
    else if (sportTitle.includes('NHL')) sportKey = 'NHL';
    
    const favoriteTeam = favoriteTeams[sportKey];
    return favoriteTeam === teamName;
  };

  // Market types configuration - dynamic based on sport
  const getMarketTypes = (sportTitle) => {
    const isBaseball = sportTitle && (sportTitle.includes('MLB') || sportTitle.includes('MiLB'));
    const isHockey = sportTitle && sportTitle.includes('NHL');
    
    let spreadLabel = 'Spread';
    if (isBaseball) {
      spreadLabel = 'Runline';
    } else if (isHockey) {
      spreadLabel = 'Puckline';
    }
    
    return {
      h2h: { label: 'Moneyline', key: 'h2h' },
      spreads: { label: spreadLabel, key: 'spreads' },
      totals: { label: 'Total', key: 'totals' },
      spreads_h1: { label: `1H ${spreadLabel}`, key: 'spreads_h1' },
      totals_h1: { label: '1H Total', key: 'totals_h1' },
      h2h_h1: { label: '1H Moneyline', key: 'h2h_h1' },
      spreads_h2: { label: `2H ${spreadLabel}`, key: 'spreads_h2' },
      totals_h2: { label: '2H Total', key: 'totals_h2' },
      h2h_h2: { label: '2H Moneyline', key: 'h2h_h2' }
    };
  };

  // Team mark: abbreviation badge in team colors (demo style, replaces logo images)
  const TeamLogo = ({ teamName, size = 24 }) => (
    <TeamBadge teamName={teamName} size={size} />
  );

  const handleGlobalTabChange = async (event, newValue) => {
    setGlobalActiveMarket(newValue);
    
    // Check if this is a period market (h1 or h2)
    const isPeriodMarket = newValue.includes('_h1') || newValue.includes('_h2');
    
    if (isPeriodMarket && odds && odds.length > 0) {
      console.log(`🔄 User selected period market: ${newValue}`);
      
      // Get only the games that will actually be displayed for this market
      const visibleGames = getVisibleGamesForMarket(newValue, true);
      console.log(`📊 ${visibleGames.length} games will be displayed for market ${newValue}`);
      
      // Fetch real period markets only for visible games that don't already have them
      const gamesToFetch = visibleGames.filter(game => 
        !gamesWithRealPeriodMarkets.has(game.id) && 
        !loadingPeriodMarkets.has(game.id)
      );
      
      if (gamesToFetch.length > 0) {
        console.log(`📡 Fetching real period markets for ${gamesToFetch.length} visible games:`, 
          gamesToFetch.map(g => `${g.away_team} @ ${g.home_team}`).join(', '));
        
        // Add all games to loading set
        const newLoadingSet = new Set(loadingPeriodMarkets);
        gamesToFetch.forEach(game => newLoadingSet.add(game.id));
        setLoadingPeriodMarkets(newLoadingSet);
        
        // Fetch period markets for each game
        const fetchPromises = gamesToFetch.map(async (game) => {
          try {
            const periodData = await fetchRealPeriodMarketsForGame(
              game.id, 
              game.sport_key, 
              'us'
            );
            
            if (periodData) {
              const mergedGame = mergeRealPeriodMarketsIntoGame(game, periodData);
              return { gameId: game.id, mergedGame };
            }
            
            return { gameId: game.id, mergedGame: null };
          } catch (error) {
            console.error(`Failed to fetch period markets for game ${game.id}:`, error);
            return { gameId: game.id, mergedGame: null };
          }
        });
        
        // Process results as they complete
        const results = await Promise.allSettled(fetchPromises);
        
        // Update state with successful results
        const newGamesWithRealPeriodMarkets = new Map(gamesWithRealPeriodMarkets);
        const updatedLoadingSet = new Set(loadingPeriodMarkets);
        
        results.forEach((result, index) => {
          const gameId = gamesToFetch[index].id;
          updatedLoadingSet.delete(gameId);
          
          if (result.status === 'fulfilled' && result.value?.mergedGame) {
            newGamesWithRealPeriodMarkets.set(gameId, result.value.mergedGame);
          }
        });
        
        setLoadingPeriodMarkets(updatedLoadingSet);
        setGamesWithRealPeriodMarkets(newGamesWithRealPeriodMarkets);
        
        console.log(`✅ Completed fetching period markets for ${results.length} games`);
      }
    }
  };

  const handleChatClick = (game, event) => {
    event.stopPropagation();
    setSelectedGame(game);
    setChatDialogOpen(true);
  };

  const handleCloseChatDialog = () => {
    setChatDialogOpen(false);
    setSelectedGame(null);
  };

  // Simple function to get sportsbook homepage URLs
  const getSportsbookHomepage = (bookmakerTitle) => {
    const homepages = {
      'DraftKings': 'https://sportsbook.draftkings.com',
      'FanDuel': 'https://sportsbook.fanduel.com',
      'BetMGM': 'https://sports.betmgm.com',
      'Caesars': 'https://www.caesars.com/sportsbook',
      'PointsBet': 'https://pointsbet.com',
      'BetRivers': 'https://ny.betrivers.com/?page=sportsbook',
      'Unibet': 'https://www.unibet.com/betting',
      'Barstool': 'https://www.barstoolsportsbook.com',
      'TwinSpires': 'https://www.twinspires.com/sportsbook',
      'William Hill': 'https://www.williamhill.com/us',
      'BetUS': 'https://www.betus.com.pa/sportsbook',
      'BetAnySports': 'https://www.betanysports.com',
      'Bovada': 'https://www.bovada.lv/sports',
      'MyBookie': 'https://mybookie.ag/sportsbook',
      'XBet': 'https://www.xbet.ag/sportsbook',
      'LowVig': 'https://www.lowvig.ag/sports',
      'BetOnline': 'https://www.betonline.ag/sportsbook',
      'Fanatics': 'https://www.fanaticssportsbook.com'
    };
    
    return homepages[bookmakerTitle] || `https://www.google.com/search?q=${encodeURIComponent(bookmakerTitle + ' sportsbook')}`;
  };

  const handleOddsClick = (game, outcome, market, bookmaker, event) => {
    event.stopPropagation();

    // If in handicapper mode and onOddsClick callback is provided, use it
    if (isHandicapperMode && onOddsClick) {
      onOddsClick(game, outcome, market, bookmaker);
      return;
    }

    // Otherwise, open sportsbook homepage in new tab (normal behavior)
    const homepage = getSportsbookHomepage(bookmaker.title);
    window.open(homepage, '_blank', 'noopener,noreferrer');
  };

  // Get bookmakers that have odds for a specific game and market
  const getBookmakersForGame = (game, marketType = 'h2h') => {
    const bookmakers = [];
    game.bookmakers?.forEach(bookmaker => {
      const market = bookmaker.markets?.find(m => m.key === marketType);
      // Only include bookmakers that have outcomes for the specified market
      if (market?.outcomes && market.outcomes.length >= 2) {
        // For moneyline markets (h2h, h2h_h1, h2h_h2), check that we have valid odds for both teams
        if (marketType === 'h2h' || marketType === 'h2h_h1' || marketType === 'h2h_h2') {
          const homeTeamOutcome = market.outcomes.find(outcome => outcome.name === game.home_team);
          const awayTeamOutcome = market.outcomes.find(outcome => outcome.name === game.away_team);

          const hasValidOdds = homeTeamOutcome &&
            awayTeamOutcome &&
            homeTeamOutcome.price &&
            typeof homeTeamOutcome.price === 'number' &&
            awayTeamOutcome.price &&
            typeof awayTeamOutcome.price === 'number';

          if (hasValidOdds) {
            bookmakers.push(bookmaker.title);
          }
        }
        // For spreads markets (spreads, spreads_h1, spreads_h2), check that we have valid odds and points for both teams
        else if (marketType === 'spreads' || marketType === 'spreads_h1' || marketType === 'spreads_h2') {
          const homeTeamOutcome = market.outcomes.find(outcome => outcome.name === game.home_team);
          const awayTeamOutcome = market.outcomes.find(outcome => outcome.name === game.away_team);

          const hasValidSpreads = homeTeamOutcome &&
            awayTeamOutcome &&
            homeTeamOutcome.price &&
            typeof homeTeamOutcome.price === 'number' &&
            awayTeamOutcome.price &&
            typeof awayTeamOutcome.price === 'number' &&
            typeof homeTeamOutcome.point === 'number' &&
            typeof awayTeamOutcome.point === 'number';

          if (hasValidSpreads) {
            bookmakers.push(bookmaker.title);
          }
        }
        // For totals markets (totals, totals_h1, totals_h2), check that we have valid odds and points for over/under
        else if (marketType === 'totals' || marketType === 'totals_h1' || marketType === 'totals_h2') {
          const overOutcome = market.outcomes.find(outcome => outcome.name === 'Over');
          const underOutcome = market.outcomes.find(outcome => outcome.name === 'Under');

          const hasValidTotals = overOutcome &&
            underOutcome &&
            overOutcome.price &&
            typeof overOutcome.price === 'number' &&
            underOutcome.price &&
            typeof underOutcome.price === 'number' &&
            typeof overOutcome.point === 'number' &&
            typeof underOutcome.point === 'number';

          if (hasValidTotals) {
            bookmakers.push(bookmaker.title);
          }
        }
      }
    });

    const sortedBookmakers = bookmakers.sort((a, b) => {
      // Safety check: if sportsbookOrder is not loaded yet, sort alphabetically
      if (!sportsbookOrder || sportsbookOrder.length === 0) {
        return a.localeCompare(b);
      }

      const indexA = sportsbookOrder.indexOf(a);
      const indexB = sportsbookOrder.indexOf(b);

      if (indexA === -1 && indexB === -1) return a.localeCompare(b); // Sort alphabetically if not in list
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });

    return sortedBookmakers.slice(0, 11);
  };

  const getOddsForTeam = (game, bookmakerTitle, teamName, marketType = 'h2h') => {
    const bookmaker = game.bookmakers?.find(b => b.title === bookmakerTitle);
    const market = bookmaker?.markets?.find(m => m.key === marketType);
    
    let outcomeToFind = teamName;
    
    // For totals market, map team names to Over/Under
    if (marketType === 'totals') {
      if (teamName === game.away_team) {
        outcomeToFind = 'Over';
      } else if (teamName === game.home_team) {
        outcomeToFind = 'Under';
      }
    }
    
    const outcome = market?.outcomes?.find(o => o.name === outcomeToFind);
    // Return null if outcome doesn't have valid price data
    return outcome && outcome.price && typeof outcome.price === 'number' ? outcome : null;
  };


  // Function to render table content based on market type
  const renderTableContent = (game, gameIndex, marketType, gameBookmakers) => {
    const isTotalsMarket = marketType === 'totals' || marketType === 'totals_h1' || marketType === 'totals_h2';
    
    if (isTotalsMarket) {
      return (
        <>
          {/* Over Row */}
          <TableRow
            sx={{
              '&:hover': { backgroundColor: 'action.hover' },
              borderBottom: 'none'
            }}
          >
            <TableCell
              sx={{
                fontWeight: 600,
                borderBottom: 'none',
                py: 1
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <TeamLogo teamName={game.away_team} sportTitle={game.sport_title} size={20} />
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  {game.away_team}
                </Typography>
                {isFavoriteTeam(game.away_team) && (
                  <StarIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                )}
              </Box>
            </TableCell>
            {gameBookmakers.map((bookmakerTitle) => {
              const outcome = getOddsForTotalsOutcome(game, bookmakerTitle, 'Over', marketType);
              const isBest = outcome ? BestOddsService.isBestTotalsOdds(game, bookmakerTitle, 'Over', marketType, gameBookmakers) : false;

              return (
                <TableCell
                  key={bookmakerTitle}
                  align="center"
                  sx={{
                    borderBottom: 'none',
                    px: 0.5,
                    py: 1
                  }}
                >
                  {outcome ? (
                    <Button
                      variant={isBest ? "contained" : "outlined"}
                      size="small"
                      onClick={(e) => handleOddsClick(game, outcome, marketType, { title: bookmakerTitle }, e)}
                      sx={{
                        minWidth: 45,
                        fontSize: '0.7rem',
                        py: 0.5,
                        px: 1,
                        textTransform: 'none',
                        backgroundColor: isBest ? 'warning.main' : 'transparent',
                        borderColor: isBest ? 'warning.main' : 'grey.300',
                        color: isBest ? 'white' : 'text.primary',
                        '&:hover': {
                          backgroundColor: isBest ? 'warning.dark' : 'primary.light',
                          color: 'white'
                        }
                      }}
                    >
                      <Box textAlign="center">
                        <Box>O {outcome.point}</Box>
                        <Box>{outcome.price > 0 ? `+${outcome.price}` : outcome.price}</Box>
                      </Box>
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      N/A
                    </Typography>
                  )}
                </TableCell>
              );
            })}
          </TableRow>

          {/* Under Row */}
          <TableRow
            sx={{
              '&:hover': { backgroundColor: 'action.hover' }
            }}
          >
            <TableCell
              sx={{
                fontWeight: 600,
                py: 1
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <TeamLogo teamName={game.home_team} sportTitle={game.sport_title} size={20} />
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  {game.home_team}
                </Typography>
                {isFavoriteTeam(game.home_team) && (
                  <StarIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                )}
              </Box>
            </TableCell>
            {gameBookmakers.map((bookmakerTitle) => {
              const outcome = getOddsForTotalsOutcome(game, bookmakerTitle, 'Under', marketType);
              const isBest = outcome ? BestOddsService.isBestTotalsOdds(game, bookmakerTitle, 'Under', marketType, gameBookmakers) : false;

              return (
                <TableCell
                  key={bookmakerTitle}
                  align="center"
                  sx={{
                    px: 0.5,
                    py: 1
                  }}
                >
                  {outcome ? (
                    <Button
                      variant={isBest ? "contained" : "outlined"}
                      size="small"
                      onClick={(e) => handleOddsClick(game, outcome, marketType, { title: bookmakerTitle }, e)}
                      sx={{
                        minWidth: 45,
                        fontSize: '0.7rem',
                        py: 0.5,
                        px: 1,
                        textTransform: 'none',
                        backgroundColor: isBest ? 'warning.main' : 'transparent',
                        borderColor: isBest ? 'warning.main' : 'grey.300',
                        color: isBest ? 'white' : 'text.primary',
                        '&:hover': {
                          backgroundColor: isBest ? 'warning.dark' : 'primary.light',
                          color: 'white'
                        }
                      }}
                    >
                      <Box textAlign="center">
                        <Box>U {outcome.point}</Box>
                        <Box>{outcome.price > 0 ? `+${outcome.price}` : outcome.price}</Box>
                      </Box>
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      N/A
                    </Typography>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </>
      );
    } else {
      // Standard team-based rows (moneyline and spreads)
      return (
        <>
          {/* Away Team Row */}
          <TableRow
            sx={{
              '&:hover': { backgroundColor: 'action.hover' },
              borderBottom: 'none'
            }}
          >
            <TableCell
              sx={{
                fontWeight: 600,
                borderBottom: 'none',
                py: 1
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <TeamLogo teamName={game.away_team} sportTitle={game.sport_title} size={20} />
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  {game.away_team}
                </Typography>
                {isFavoriteTeam(game.away_team) && (
                  <StarIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                )}
              </Box>
            </TableCell>
            {gameBookmakers.map((bookmakerTitle) => {
              const outcome = getOddsForTeam(game, bookmakerTitle, game.away_team, marketType);
              const isBest = outcome ? BestOddsService.isBestOdds(game, bookmakerTitle, game.away_team, marketType, gameBookmakers) : false;

              return (
                <TableCell
                  key={bookmakerTitle}
                  align="center"
                  sx={{
                    borderBottom: 'none',
                    px: 0.5,
                    py: 1
                  }}
                >
                  {outcome ? (
                    <Button
                      variant={isBest ? "contained" : "outlined"}
                      size="small"
                      onClick={(e) => handleOddsClick(game, outcome, marketType, { title: bookmakerTitle }, e)}
                      sx={{
                        minWidth: 45,
                        fontSize: '0.7rem',
                        py: 0.5,
                        px: 1,
                        backgroundColor: isBest ? 'warning.main' : 'transparent',
                        borderColor: isBest ? 'warning.main' : 'grey.300',
                        color: isBest ? 'white' : 'text.primary',
                        '&:hover': {
                          backgroundColor: isBest ? 'warning.dark' : 'primary.light',
                          color: 'white'
                        }
                      }}
                    >
                      {(marketType === 'spreads' || marketType === 'spreads_h1' || marketType === 'spreads_h2') && outcome.point !== undefined ? (
                        <Box textAlign="center">
                          <Box>{outcome.point > 0 ? `+${outcome.point}` : outcome.point}</Box>
                          <Box>{outcome.price > 0 ? `+${outcome.price}` : outcome.price}</Box>
                        </Box>
                      ) : (
                        <Box>{outcome.price > 0 ? `+${outcome.price}` : outcome.price}</Box>
                      )}
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      N/A
                    </Typography>
                  )}
                </TableCell>
              );
            })}
          </TableRow>

          {/* Home Team Row */}
          <TableRow
            sx={{
              '&:hover': { backgroundColor: 'action.hover' }
            }}
          >
            <TableCell
              sx={{
                fontWeight: 600,
                py: 1
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <TeamLogo teamName={game.home_team} sportTitle={game.sport_title} size={20} />
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  {game.home_team}
                </Typography>
                {isFavoriteTeam(game.home_team) && (
                  <StarIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                )}
              </Box>
            </TableCell>
            {gameBookmakers.map((bookmakerTitle) => {
              const outcome = getOddsForTeam(game, bookmakerTitle, game.home_team, marketType);
              const isBest = outcome ? BestOddsService.isBestOdds(game, bookmakerTitle, game.home_team, marketType, gameBookmakers) : false;

              return (
                <TableCell
                  key={bookmakerTitle}
                  align="center"
                  sx={{
                    px: 0.5,
                    py: 1
                  }}
                >
                  {outcome ? (
                    <Button
                      variant={isBest ? "contained" : "outlined"}
                      size="small"
                      onClick={(e) => handleOddsClick(game, outcome, marketType, { title: bookmakerTitle }, e)}
                      sx={{
                        minWidth: 45,
                        fontSize: '0.7rem',
                        py: 0.5,
                        px: 1,
                        backgroundColor: isBest ? 'warning.main' : 'transparent',
                        borderColor: isBest ? 'warning.main' : 'grey.300',
                        color: isBest ? 'white' : 'text.primary',
                        '&:hover': {
                          backgroundColor: isBest ? 'warning.dark' : 'primary.light',
                          color: 'white'
                        }
                      }}
                    >
                      {(marketType === 'spreads' || marketType === 'spreads_h1' || marketType === 'spreads_h2') && outcome.point !== undefined ? (
                        <Box textAlign="center">
                          <Box>{outcome.point > 0 ? `+${outcome.point}` : outcome.point}</Box>
                          <Box>{outcome.price > 0 ? `+${outcome.price}` : outcome.price}</Box>
                        </Box>
                      ) : (
                        <Box>{outcome.price > 0 ? `+${outcome.price}` : outcome.price}</Box>
                      )}
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      N/A
                    </Typography>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </>
      );
    }
  };

  if (!odds || odds.length === 0) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h6" color="text.secondary">
          No odds data available
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          Configure your The Odds API key to start viewing live odds
        </Typography>
      </Box>
    );
  }

  // Process games, using real period market data when available
  const processedGames = odds.map(game => {
    // Use real period market data if available, otherwise use original game data
    return gamesWithRealPeriodMarkets.get(game.id) || game;
  });

  // Filter out games that don't have any valid odds for any market
  const gamesWithOdds = processedGames.filter(game => {
    const hasMoneyline = getBookmakersForGame(game, 'h2h').length > 0;
    const hasSpreads = getBookmakersForGame(game, 'spreads').length > 0;
    const hasTotals = getBookmakersForGame(game, 'totals').length > 0;
    return hasMoneyline || hasSpreads || hasTotals;
  });

  if (gamesWithOdds.length === 0) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h6" color="text.secondary">
          No games with odds available
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1}>
          All games loaded but none have complete betting odds data
        </Typography>
      </Box>
    );
  }

  // Get all available markets across all games
  const getAllAvailableMarkets = (games) => {
    const allMarkets = new Set();
    games.forEach(game => {
      if (getBookmakersForGame(game, 'spreads').length > 0) allMarkets.add('spreads');
      if (getBookmakersForGame(game, 'totals').length > 0) allMarkets.add('totals');
      if (getBookmakersForGame(game, 'h2h').length > 0) allMarkets.add('h2h');
      if (getBookmakersForGame(game, 'spreads_h1').length > 0) allMarkets.add('spreads_h1');
      if (getBookmakersForGame(game, 'totals_h1').length > 0) allMarkets.add('totals_h1');
      if (getBookmakersForGame(game, 'h2h_h1').length > 0) allMarkets.add('h2h_h1');
      if (getBookmakersForGame(game, 'spreads_h2').length > 0) allMarkets.add('spreads_h2');
      if (getBookmakersForGame(game, 'totals_h2').length > 0) allMarkets.add('totals_h2');
      if (getBookmakersForGame(game, 'h2h_h2').length > 0) allMarkets.add('h2h_h2');
    });
    
    const availableMarkets = Array.from(allMarkets);
    
    // For baseball (MLB/MiLB), order should be: Moneyline, Total, Runline + period markets
    const isBaseball = selectedSport && (selectedSport.title.includes('MLB') || selectedSport.title.includes('MiLB'));
    if (isBaseball) {
      const baseballOrder = ['h2h', 'totals', 'spreads', 'h2h_h1', 'totals_h1', 'spreads_h1', 'h2h_h2', 'totals_h2', 'spreads_h2'];
      return baseballOrder.filter(market => availableMarkets.includes(market));
    }
    
    // Default order for all other sports: Spread, Total, Moneyline + period markets
    const defaultOrder = ['spreads', 'totals', 'h2h', 'spreads_h1', 'totals_h1', 'h2h_h1', 'spreads_h2', 'totals_h2', 'h2h_h2'];
    return defaultOrder.filter(market => availableMarkets.includes(market));
  };

  return (
    <Box>
      {/* Global Market Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={globalActiveMarket}
          onChange={handleGlobalTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ 
            minHeight: 36,
            '& .MuiTabs-flexContainer': {
              justifyContent: 'flex-start'
            },
            '& .MuiTabs-scrollButtons': {
              '&.Mui-disabled': {
                opacity: 0.3
              }
            }
          }}
        >
          {getAllAvailableMarkets(gamesWithOdds).map(marketKey => {
            let marketTypes;
            if (selectedSport) {
              marketTypes = getMarketTypes(selectedSport.title);
            } else {
              marketTypes = {
                h2h: { label: 'Moneyline', key: 'h2h' },
                spreads: { label: 'Spread', key: 'spreads' },
                totals: { label: 'Total', key: 'totals' },
                spreads_h1: { label: '1H Spread', key: 'spreads_h1' },
                totals_h1: { label: '1H Total', key: 'totals_h1' },
                h2h_h1: { label: '1H Moneyline', key: 'h2h_h1' },
                spreads_h2: { label: '2H Spread', key: 'spreads_h2' },
                totals_h2: { label: '2H Total', key: 'totals_h2' },
                h2h_h2: { label: '2H Moneyline', key: 'h2h_h2' }
              };
            }
            
            const isPeriodMarket = marketKey.includes('_h1') || marketKey.includes('_h2');
            const isLoadingPeriodData = isPeriodMarket && loadingPeriodMarkets.size > 0;
            const tabLabel = isLoadingPeriodData ? `${marketTypes[marketKey].label} ⏳` : marketTypes[marketKey].label;
            
            return (
              <Tab
                key={marketKey}
                value={marketKey}
                label={tabLabel}
                sx={{
                  minHeight: 32,
                  py: 0.5,
                  px: 2,
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  textTransform: 'none'
                }}
              />
            );
          })}
        </Tabs>
      </Box>

      {(() => {
        let filteredGames = gamesWithOdds.filter(game => getBookmakersForGame(game, globalActiveMarket).length > 0);
        
        // Sort games to prioritize favorite teams
        filteredGames = filteredGames.sort((gameA, gameB) => {
          const aHasFavorite = isFavoriteTeam(gameA.home_team) || isFavoriteTeam(gameA.away_team);
          const bHasFavorite = isFavoriteTeam(gameB.home_team) || isFavoriteTeam(gameB.away_team);
          
          // Prioritize games with favorite teams
          if (aHasFavorite && !bHasFavorite) return -1;
          if (!aHasFavorite && bHasFavorite) return 1;
          
          // If both or neither have favorites, maintain original order
          return 0;
        });
        
        // Apply maxGames limit if specified
        if (maxGames && maxGames > 0) {
          filteredGames = filteredGames.slice(0, maxGames);
        }
        
        const elements = [];
        
        filteredGames.forEach((game, gameIndex) => {
          const gameBookmakers = getBookmakersForGame(game, globalActiveMarket);
          const gameHasFavorite = isFavoriteTeam(game.home_team) || isFavoriteTeam(game.away_team);

          // Add the game card
          elements.push(
            <Card 
              key={`game-${gameIndex}`} 
              elevation={gameHasFavorite ? 3 : 1} 
              sx={{ 
                mb: 2, 
                overflow: 'hidden',
                border: gameHasFavorite ? '2px solid' : 'none',
                borderColor: gameHasFavorite ? 'warning.main' : 'transparent',
                position: 'relative'
              }}
            >
              {/* Game Header */}
              {/*}
              <Box
                sx={{
                  backgroundColor: 'grey.50',
                  p: 2,
                  borderBottom: 1,
                  borderColor: 'divider'
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {game.away_team} @ {game.home_team}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Chip
                        icon={<SportIcon sportTitle={game.sport_title} fontSize="small" />}
                        label={game.sport_title}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={new Date(game.commence_time).toLocaleDateString()}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={new Date(game.commence_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            */}
              {/* Odds Table */}
              <TableContainer>
                <Table size="small" sx={{ minWidth: 300 }}>
                  <TableHead>
                    <TableRow sx={{ 
                      backgroundColor: theme.palette.mode === 'dark' ? '#2d5a4a' : 'grey.100'
                    }}>
                      <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>
                        <Box display="flex" gap={1} alignItems="center">
                          <Chip
                            icon={<SportIcon sportTitle={game.sport_title} fontSize="small" />}
                            label={game.sport_title}
                            size="small"
                            sx={{
                              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                              borderColor: theme.palette.mode === 'dark' ? 'white' : 'grey.400',
                            }}
                            variant="outlined"
                          />
                          <Chip
                            label={new Date(game.commence_time).toLocaleDateString()}
                            size="small"
                            sx={{
                              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                              borderColor: theme.palette.mode === 'dark' ? 'white' : 'grey.400',
                            }}
                            variant="outlined"
                          />
                          <Chip
                            label={new Date(game.commence_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            size="small"
                            sx={{
                              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                              borderColor: theme.palette.mode === 'dark' ? 'white' : 'grey.400',
                            }}
                            variant="outlined"
                          />
                          {gameHasFavorite && (
                            <Chip
                              icon={<StarIcon />}
                              label="Favorite"
                              size="small"
                              color="warning"
                              variant="filled"
                            />
                          )}
                          <Tooltip title="Join game chat">
                            <IconButton
                              onClick={(e) => handleChatClick(game, e)}
                              size="small"
                            >
                              <ChatIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      {gameBookmakers.map((bookmaker) => (
                        <TableCell
                          key={bookmaker}
                          align="center"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            px: 0.5,
                            minWidth: 80
                          }}
                        >
                          {bookmaker}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {renderTableContent(game, gameIndex, globalActiveMarket, gameBookmakers)}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          );

          // Insert sports news after the 10th game, before promos
          if (gameIndex === 9 && filteredGames.length >= 10) {
            elements.push(
              <Box key={`sports-news-${gameIndex}`} sx={{ mb: 2 }}>
                <SportsNews />
              </Box>
            );
            elements.push(
              <Box key={`promo-${gameIndex}`} sx={{ mb: 2 }}>
                <Promos />
              </Box>
            );
          }
        });

        return elements;
      })()}

      {/* Game Chat Dialog */}
      <GameChatDialog
        open={chatDialogOpen}
        onClose={handleCloseChatDialog}
        game={selectedGame}
      />
    </Box>
  );
};

export default OddsList;
